/**
 * Which app routes would get the right-side MJ rail versus the floating bubble.
 *
 * `pathHasRail` answers "would this route have the rail", independent of the flag.
 * Whether the rail is actually mounted is `chatSurfaceFor` below: the rail only
 * appears when the build-time `VITE_CHAT_SURFACE` is `'rail'`; by default every
 * page gets the floating bubble instead.
 *
 * Excluded from the rail (locked design, Carl 2026-09-29):
 *  - the editor (`/editor/...`) — its MJ lives in an inspector tab
 *  - the full chat page (`/chat`) — stays its own page
 *  - settings routes (`/settings...`) — keep the floating bubble
 *
 * In rail mode every other app page gets the rail through the layout.
 */
export function pathHasRail(pathname: string): boolean {
  if (pathname.startsWith('/editor/')) return false;
  if (pathname === '/chat' || pathname.startsWith('/chat/') || pathname.startsWith('/chat?')) {
    return false;
  }
  if (pathname.startsWith('/settings')) return false;
  return true;
}

function isEditorOrChatRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/editor/') ||
    pathname === '/chat' ||
    pathname.startsWith('/chat/') ||
    pathname.startsWith('/chat?')
  );
}

/**
 * Which chat surface the layout mounts for a route, given the build-time
 * `VITE_CHAT_SURFACE` choice (see `CHAT_SURFACE` in config/environment.ts).
 *
 *  - `'none'`: the editor (its MJ is an inspector tab) and `/chat` (its own page).
 *    Nothing is mounted in any mode.
 *  - `'rail'`: only when the surface is exactly `'rail'` AND the route has a rail
 *    (`pathHasRail`); settings keeps the bubble in that mode.
 *  - `'bubble'`: when the surface is exactly `'bubble'`, and settings in rail mode.
 *  - `'header'`: every other page in every other case, which is the DEFAULT (an unset or
 *    unknown surface), settings included: the top bar exists there.
 *
 * `pathHasRail` keeps answering "would this route have the rail"; this adds the flag.
 */
export function chatSurfaceFor(pathname: string, surface: unknown): 'rail' | 'bubble' | 'header' | 'none' {
  if (isEditorOrChatRoute(pathname)) return 'none';
  if (surface === 'rail') return pathHasRail(pathname) ? 'rail' : 'bubble';
  if (surface === 'bubble') return 'bubble';
  return 'header';
}
